import { IContentComponent, IVersionSection } from "../../../models/Interfaces";

export interface ComponentRevision {
  generation: number;
  component: IContentComponent;
}

export const replaceSectionComponents = (
  sections: IVersionSection[],
  sectionId: number,
  updater: (components: IContentComponent[]) => IContentComponent[]
): IVersionSection[] => {
  let changed = false;

  const next = sections.map((section) => {
    if (section.Id !== sectionId) {
      return section;
    }

    const components = updater(section.ContentComponents ?? []);

    if (components === section.ContentComponents) {
      return section;
    }

    changed = true;
    return {
      ...section,
      ContentComponents: components
    };
  });

  return changed ? next : sections;
};

export const withLiveLayout = (
  component: IContentComponent,
  live: IContentComponent | undefined
): IContentComponent => {
  if (!live) {
    return component;
  }

  return {
    ...component,
    PosX: live.PosX,
    PosY: live.PosY,
    Cols: live.Cols,
    Rows: live.Rows,
    ColIndex: live.ColIndex,
    x: live.x,
    y: live.y,
    w: live.w,
    h: live.h
  };
};

export const layoutsMatch = (left: IContentComponent, right: IContentComponent): boolean =>
  left.PosX === right.PosX
  && left.PosY === right.PosY
  && left.Cols === right.Cols
  && left.Rows === right.Rows
  && left.ColIndex === right.ColIndex;

const removedAfterFetch = (
  removedAt: number | undefined,
  fetchGeneration: number
): boolean => removedAt !== undefined && removedAt > fetchGeneration;

export const mergeFetchedSections = (
  fetched: IVersionSection[],
  current: IVersionSection[],
  fetchGeneration: number,
  revisions: Map<number, ComponentRevision>,
  removedComponents: Map<number, number>,
  removedSections: Map<number, number>
): IVersionSection[] => {
  const currentById = new Map(current.map((section) => [section.Id, section]));

  return fetched
    .filter((section) => !removedAfterFetch(removedSections.get(section.Id), fetchGeneration))
    .map((section) => {
      const previousComponents = currentById.get(section.Id)?.ContentComponents ?? [];
      const components = new Map<number, IContentComponent>();

      (section.ContentComponents ?? []).forEach((component) => {
        if (!removedAfterFetch(removedComponents.get(component.Id), fetchGeneration)) {
          components.set(component.Id, component);
        }
      });

      previousComponents.forEach((component) => {
        if (removedAfterFetch(removedComponents.get(component.Id), fetchGeneration)) {
          components.delete(component.Id);
          return;
        }

        const revision = revisions.get(component.Id);

        if (revision && revision.generation > fetchGeneration) {
          components.set(component.Id, revision.component);
        }
      });

      return {
        ...section,
        ContentComponents: Array.from(components.values())
      };
    });
};

/** Mirror the server's one-based ordering after successful section removal. */
export const reindexSections = <T extends Pick<IVersionSection, "Id" | "Order">>(sections: T[]): T[] =>
  [...sections].sort((left, right) => left.Order - right.Order)
    .map((section, index) => section.Order === index + 1 ? section : { ...section, Order: index + 1 });

export const sectionInsertionOrder = (sections: ReadonlyArray<Pick<IVersionSection, "Id" | "Order">>, sectionId: number, above: boolean): number | null => {
  const index = [...sections].sort((left, right) => left.Order - right.Order).findIndex(section => section.Id === sectionId);
  return index < 0 ? null : index + (above ? 1 : 2);
};
